import {ModalToolContainerService, WebmateToolConfig} from "@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service";
import {ArtifactId, AuditId, BlobId, BrowserSessionStateId, ProjectId} from "@webmate-web-core/shared/types/ids";
import {Component, Inject, ViewChild} from "@angular/core";
import {TOOL_CONTAINER_DATA} from "@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.component";
import {FormBuilder, FormsModule, ReactiveFormsModule, UntypedFormGroup} from "@angular/forms";
import {FormsCommonsModule} from "@webmate-web-core/shared/components/forms-commons/forms-commons.module";

import {FontAwesomeModule} from "@fortawesome/angular-fontawesome";
import {ReportService} from "@webmate-web-core/shared/services/report.service";
import {TabViewModule} from "primeng/tabview";
import {IconDefinition} from "@fortawesome/fontawesome-svg-core";
import {ButtonModule} from "primeng/button";
import {DomSanitizer, SafeResourceUrl, SafeUrl} from "@angular/platform-browser";
import {faCheckCircle, faDownload, faEdit, faHammer, faPrint, faShare, faSyncAlt} from '@fortawesome/pro-solid-svg-icons';
import {ReportId, Report, ReportOutput} from '../../../../../report/report_types';
import {RouterLink} from '@angular/router';
import {SplitButton} from 'primeng/splitbutton';
import {ReportInfo, ReportOutputType} from '../../../../../report/types';
import {Observable, of} from 'rxjs';
import {filter, map, switchMap, take, tap, timeout} from 'rxjs/operators';
import {EditReportToolComponent, EditReportToolConfig} from '@webmate-web-core/projects/project/auditlab/audits/edit-report-tool.component';
import {ReportSharingToolComponent, ReportSharingToolConfig} from '@webmate-web-core/projects/project/auditlab/audits/report-sharing-tool.component';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {JsonPipe} from '@angular/common';

export class PreviewReportToolConfig implements WebmateToolConfig {
    scrollable = true;

    constructor(public reportId: ReportId, public toolName: string = 'Preview Report') {}
}

// from MDN docs
function setPrint(this: HTMLIFrameElement) {
    if (!this.contentWindow) {
        return;
    }

    const closePrint = () => {
        document.body.removeChild(this);
    };
    this.contentWindow.onbeforeunload = closePrint;
    this.contentWindow.onafterprint = closePrint;
    this.contentWindow.print();
}

@UntilDestroy()
@Component({
    selector: 'wm-preview-report-tool',
    templateUrl: './preview-report-tool.component.html',
    styleUrls: ['./preview-report-tool.component.less'],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, FormsCommonsModule, FontAwesomeModule, TabViewModule, ButtonModule, RouterLink, SplitButton, JsonPipe]
})
export class PreviewReportToolComponent {

    @ViewChild('previewFrame', {static: false}) previewFrame: HTMLIFrameElement;

    reportId: ReportId;
    report?: ReportInfo;
    pdfOutput?: ReportOutput;
    previewUrl?: SafeResourceUrl;

    constructor(@Inject(TOOL_CONTAINER_DATA) public config: PreviewReportToolConfig, private toolContainerService: ModalToolContainerService,
                private domSanitizer: DomSanitizer, private reportService: ReportService) {
        this.reportId = config.reportId;
    }

    ngOnInit() {
        this.reportService.getReportPreviewUrl$(this.reportId).pipe(
        ).subscribe(previewUrl => {
            this.previewUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(previewUrl);
        });


        this.reportService.getReportInfo$(this.reportId).pipe(
            untilDestroyed(this),
            tap(report => this.reportService.registerEventsForProject(report.projectId))
        ).subscribe(report => {
            if (!report) {
                console.warn(`Report ${this.reportId} not found.`);
                return;
            }
            this.report = report;
            this.pdfOutput = report.outputs['pdf'];
        });
    }

    normalizeReportName(reportName: string) {
        return reportName.replace(/[^a-zA-Z0-9]/g, '_');
    }

    getPdfOutput(): Observable<ReportOutput | undefined> {
        if (!this.report) {
            return of(undefined);
        }
        if (this.pdfOutput?.state === 'ready') {
            return of(this.pdfOutput);
        } else {
            return this.reportService.generateOutput(this.reportId, 'pdf');
        }
    }

    downloadPdf() {
        if (!this.report) {
            console.warn(`Report ${this.reportId} not found.`);
            return;
        }

        const reportTitle = this.report.title || 'unknown';

        this.getPdfOutput().pipe(
            filter(output => !!output && output.state === 'ready' && !!output.blobId),
            timeout(20000),
            switchMap((output: ReportOutput | undefined) => {
                if (!output || output.state !== 'ready' || !output.blobId) {
                    console.warn(`Report ${this.report} does not have output of type 'pdf' ready for download. Must be generated first.`);
                    return of(undefined);
                }
                return this.reportService.getReportContentBlob$(this.reportId, output.blobId).pipe(map(blob => {
                    const data = URL.createObjectURL(blob)
                    const link = document.createElement('a');
                    link.setAttribute('href', data);
                    link.setAttribute('download', `report_${this.normalizeReportName(reportTitle)}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }))
            })
        ).subscribe( {
            next: output => {
            },
            error: err => {
                if (err.name === 'TimeoutError') {
                    console.error('Download timed out:', err);
                }
            },
        });
    }

    editReport() {
        if (!this.report) {
            console.warn(`Report ${this.reportId} not found.`);
            return;
        }
        this.toolContainerService.openTool(EditReportToolComponent, new EditReportToolConfig('Edit Report', this.reportId, this.report.projectId, (reportId) => {}));
    }

    shareReport() {
        this.toolContainerService.openTool(ReportSharingToolComponent, new ReportSharingToolConfig(this.reportId, 'Share Report'));
    }

    protected readonly faPrint = faPrint;
    protected readonly faEdit = faEdit;
    protected readonly faShare = faShare;
    protected readonly faCheckCircle = faCheckCircle;
    protected readonly faHammer = faHammer;
    protected readonly faSyncAlt = faSyncAlt;
    protected readonly faDownload = faDownload;
}
